import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';

import AddRfid from './AddRfid';
import AddRfidSuccess from './AddRfidSuccess';
import RfidInstructions from './RfidInstructions';

export type AddRfidStage = 'name' | 'instructions' | 'success';

export interface AddRfidModalState {
  stage: AddRfidStage;
  name: string;
}

export interface AddRfidModalStageProps {
  open: boolean;
  state: AddRfidModalState;
  groupId: number | null;
  setState: Dispatch<SetStateAction<AddRfidModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
}

const StageComponents: Record<AddRfidStage, FC<AddRfidModalStageProps>> = {
  name: AddRfid,
  instructions: RfidInstructions,
  success: AddRfidSuccess,
};

export interface AddRfidModalProps extends DefaultPopupProps {
  groupId?: number;
}

const AddRfidModal: FC<AddRfidModalProps> = ({ open, onClose, groupId }) => {
  const { t } = useTranslation();

  const [state, setState] = useState<AddRfidModalState>({
    stage: 'name',
    name: '',
  });

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, name: '' }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    if (state.stage === 'name') {
      setState(prev => ({ ...prev, name: '' }));

      onClose();
    } else {
      setState(prev => ({ ...prev, stage: 'name' }));
    }
  }, [onClose, state]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'name' ? <ArrowBackRoundedIcon /> : undefined
      }
      header={
        state.stage !== 'name' && state.stage !== 'success'
          ? t('common:pages.rfids.add_new_rfid')
          : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <StageComponent
        open={open}
        setState={setState}
        state={state}
        groupId={groupId ?? null}
        onClose={onClose}
        onCloseProxy={onCloseProxy}
      />
    </Modal>
  );
};

export default AddRfidModal;
